import { Fragment, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * A button that opens a short list of choices, each with a line saying what it
 * means — "Go live: their book here is the real one" rather than a bare verb.
 *
 * Built for the business header's "Change stage" and kept general because
 * phase B wants the same shape elsewhere. The behaviour is the menu-button
 * pattern: the trigger says it has a popup and whether it is open; arrow keys
 * move between items; Esc and a click outside close it; picking an item closes
 * it and puts focus back on the trigger **before** the item's action runs — so
 * a confirm dialog opened by that action remembers the trigger as the place to
 * return focus to, instead of a menu item that no longer exists.
 *
 * `separator` draws a rule above an item; `tone: 'danger'` is for the one exit
 * that does not come back.
 */
export interface MenuItem {
  key: string;
  label: string;
  hint?: string | undefined;
  tone?: 'danger' | undefined;
  separator?: boolean | undefined;
  onPick: () => void;
}

export function Menu({
  label,
  items,
  disabled = false,
  ariaLabel,
}: {
  label: string;
  items: readonly MenuItem[];
  disabled?: boolean;
  /** The trigger's name for a screen reader, when `label` is only a glyph ("⋯"). */
  ariaLabel?: string | undefined;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const away = (e: MouseEvent): void => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const all = [...(list.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    const at = all.findIndex((el) => el === document.activeElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    all[(at + step + all.length) % all.length]?.focus();
  };

  return (
    <div className="menu" ref={root}>
      <button
        ref={trigger}
        type="button"
        className="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
      >
        {label} <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className="menu__list" role="menu" id={id} ref={list} onKeyDown={onKeyDown}>
          {items.map((item) => (
            <Fragment key={item.key}>
              {item.separator === true ? <hr /> : null}
              <button
                type="button"
                role="menuitem"
                className={item.tone === 'danger' ? 'is-danger' : undefined}
                onClick={() => {
                  close();
                  item.onPick();
                }}
              >
                <strong>{item.label}</strong>
                {item.hint === undefined ? null : <small>{item.hint}</small>}
              </button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
